import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Audience } from '@epasu/core';
import {
  ORGANIZATION_STATUSES,
  ORGANIZATION_STATUS_LABELS,
  type OrganizationStatus,
} from '@epasu/core/constants';
import { Building2, Handshake, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { crm, type OrganizationRow } from '@/lib/crm-client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { NewCustomerDialog } from '@/components/NewCustomerDialog';
import { OrganizationDialog } from '@/components/OrganizationDialog';
import { PromoteCustomerDialog } from '@/components/PromoteCustomerDialog';
import { PageHeader } from '@/components/PageHeader';
import { useUrlState } from '@/lib/useUrlState';
import { cn } from '@/lib/utils';

/**
 * Das Verzeichnis zeigt zuerst die Kunden.
 *
 * Die Lead-Importe bringen hunderte Firmen mit, die noch nie jemand
 * angesprochen hat; als Vorgabe begrüßten sie die wenigen, mit denen
 * tatsächlich gearbeitet wird, unter sich. Leads haben auf der Akquise-Seite
 * ihr eigenes Zuhause und sind hier mit einem Klick auf „Leads“ erreichbar.
 */
const STATUS_VORGABE: OrganizationStatus = 'kunde';

/** „Alle“ ist kein Firmenstatus, sondern das Weglassen des Filters. */
type StatusFilter = OrganizationStatus | 'alle';
const STATUS_FILTER: readonly StatusFilter[] = ['alle', ...ORGANIZATION_STATUSES];

/** Die Pluralform auf den Filterschaltflächen; die Chips in der Tabelle nennen den Einzelstatus. */
const FILTER_LABELS: Record<StatusFilter, string> = {
  alle: 'Alle',
  lead: 'Leads',
  im_gespraech: ORGANIZATION_STATUS_LABELS.im_gespraech,
  kunde: 'Kunden',
  ruht: ORGANIZATION_STATUS_LABELS.ruht,
  ehemalig: ORGANIZATION_STATUS_LABELS.ehemalig,
  verloren: ORGANIZATION_STATUS_LABELS.verloren,
};

export const STATUS_VARIANT: Record<
  OrganizationStatus,
  'neutral' | 'info' | 'success' | 'warning' | 'danger'
> = {
  lead: 'neutral',
  im_gespraech: 'info',
  kunde: 'success',
  ruht: 'warning',
  ehemalig: 'warning',
  verloren: 'danger',
};

export function OrganizationsPage() {
  const navigate = useNavigate();

  const [rows, setRows] = useState<OrganizationRow[]>([]);
  const [audiences, setAudiences] = useState<Audience[]>([]);
  // Beides steht in der URL: ein weitergereichter Link zeigt dieselbe Liste,
  // und „Zurück“ aus einer Firmenakte landet wieder im selben Filter.
  const [status, setStatus] = useUrlState<StatusFilter>(
    'status',
    STATUS_VORGABE,
    STATUS_FILTER,
  );
  const [audienceIdUrl, setAudienceId] = useUrlState<string>('zielgruppe', '');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const [editing, setEditing] = useState<OrganizationRow | 'new' | null>(null);
  const [deleting, setDeleting] = useState<OrganizationRow | null>(null);
  const [promoting, setPromoting] = useState<OrganizationRow | null>(null);
  const [neuerKunde, setNeuerKunde] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  // Eine Zielgruppe, die es nicht mehr gibt (gelöscht, handgeschriebener Link),
  // filtert nicht: sonst stünde die Liste leer, während das Auswahlfeld
  // „Alle Zielgruppen“ behauptet. Solange die Zielgruppen noch laden, gilt die
  // URL, damit die erste Abfrage nicht ungefiltert losgeht.
  const audienceId =
    audiences.length === 0 || audiences.some((a) => a.id === audienceIdUrl)
      ? audienceIdUrl
      : '';

  const load = useCallback(async () => {
    setRows(
      await crm.organizations.list({
        ...(audienceId ? { audienceId } : {}),
        ...(search.trim() ? { search: search.trim() } : {}),
        ...(status !== 'alle' ? { status } : {}),
      }),
    );
    setLoading(false);
  }, [audienceId, search, status]);

  const erster = useRef(true);
  useEffect(() => {
    if (erster.current) {
      erster.current = false;
      void load();
      return;
    }
    const t = setTimeout(() => void load(), 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    void crm.audiences.list().then(setAudiences);
  }, []);

  const audienceName = useMemo(
    () => new Map(audiences.map((a) => [a.id, a.name])),
    [audiences],
  );

  /**
   * Zielgruppe direkt in der Zeile umstellen.
   *
   * Eine gewachsene Liste einzusortieren heißt, dieselbe Handbewegung
   * dutzendfach zu machen. Über den Bearbeiten-Dialog wären das jedes Mal
   * vier Klicks — hier ist es einer.
   */
  const zielgruppeSetzen = async (firma: OrganizationRow, neu: string) => {
    if (neu === firma.audienceId) return;
    setFehler(null);
    // Sofort umstellen, sonst springt die Auswahl zurück, solange der Server
    // antwortet.
    setRows((bisher) =>
      bisher.map((r) => (r.id === firma.id ? { ...r, audienceId: neu } : r)),
    );
    try {
      await crm.organizations.update(firma.id, { audienceId: neu });
      await load();
    } catch (err) {
      setRows((bisher) =>
        bisher.map((r) =>
          r.id === firma.id ? { ...r, audienceId: firma.audienceId } : r,
        ),
      );
      setFehler(
        err instanceof Error
          ? `${firma.name}: ${err.message}`
          : `${firma.name}: Die Zielgruppe ließ sich nicht ändern.`,
      );
    }
  };

  const suche = search.trim().length > 0;
  const gefiltert = suche || status !== 'alle' || audienceId !== '';

  return (
    <div>
      <PageHeader className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[16rem] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Name, Domain oder Ort …"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Firmen durchsuchen"
            />
          </div>

          <select
            className="h-10 rounded-md border border-input bg-card px-3 text-sm"
            value={audienceId}
            onChange={(e) => setAudienceId(e.target.value)}
            aria-label="Zielgruppe"
          >
            <option value="">Alle Zielgruppen</option>
            {audiences.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>

          <Button variant="outline" onClick={() => setNeuerKunde(true)}>
            <Handshake className="mr-2 h-4 w-4" />
            Neuer Kunde
          </Button>

          <Button onClick={() => setEditing('new')}>
            <Plus className="mr-2 h-4 w-4" />
            Neue Firma
          </Button>
        </div>

        <div
          className="flex flex-wrap gap-1.5"
          role="group"
          aria-label="Status"
        >
          {STATUS_FILTER.map((s) => (
            <button
              key={s}
              type="button"
              aria-pressed={status === s}
              onClick={() => setStatus(s)}
              className={cn(
                'rounded-full border px-3 py-1 text-sm transition-colors',
                status === s
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-input bg-card text-muted-foreground hover:bg-muted/60',
              )}
            >
              {FILTER_LABELS[s]}
            </button>
          ))}
        </div>
      </PageHeader>

      {fehler && (
        <p className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {fehler}
        </p>
      )}

      <Card className="overflow-hidden">
        {loading ? (
          <p className="p-6 text-sm text-muted-foreground">Lade …</p>
        ) : rows.length === 0 ? (
          <div className="space-y-3 p-8 text-center">
            <p className="text-sm text-muted-foreground">
              {suche
                ? 'Keine Firma passt zur Suche.'
                : gefiltert
                  ? 'Keine Firma passt zu diesen Filtern.'
                  : 'Noch keine Firmen. Sie entstehen auch automatisch, wenn du bei einem Kontakt einen Firmennamen einträgst.'}
            </p>
            {!suche && !gefiltert && (
              <Button onClick={() => setEditing('new')}>
                <Plus className="mr-2 h-4 w-4" />
                Erste Firma anlegen
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-medium">Firma</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Zielgruppe</th>
                  <th className="px-4 py-3 font-medium">Ort</th>
                  <th className="px-4 py-3 font-medium">Kontakte</th>
                  <th className="px-4 py-3 font-medium">Offene Deals</th>
                  <th className="px-4 py-3 font-medium">Offene Aufträge</th>
                  <th className="px-4 py-3 font-medium">Letzter Kontakt</th>
                  <th className="w-24 px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {rows.map((o) => (
                  <tr
                    key={o.id}
                    className="cursor-pointer border-b last:border-0 hover:bg-muted/40"
                    onClick={() =>
                      navigate(`/firmen/${o.id}`, { state: { from: '/firmen' } })
                    }
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 font-medium">
                        <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                        {o.name}
                        {o.unseenMessages > 0 && (
                          <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[11px] font-normal text-primary">
                            {o.unseenMessages} neu
                          </span>
                        )}
                      </div>
                      {o.domain && (
                        <span className="ml-6 text-xs text-muted-foreground">
                          {o.domain}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={STATUS_VARIANT[o.status]}>
                        {ORGANIZATION_STATUS_LABELS[o.status]}
                      </Badge>
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <select
                        className="h-8 rounded-md border border-input bg-card px-2 text-xs"
                        value={o.audienceId}
                        onChange={(e) => void zielgruppeSetzen(o, e.target.value)}
                        aria-label={`Zielgruppe von ${o.name}`}
                      >
                        {audiences.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{o.city ?? '—'}</td>
                    <td className="px-4 py-3 text-muted-foreground">{o.contactCount}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {o.openDealCount || '—'}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {o.openOrderCount || '—'}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {o.lastContactAt
                        ? new Date(o.lastContactAt).toLocaleDateString('de-DE')
                        : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div
                        className="flex justify-end gap-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {!o.isCustomer && (
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`${o.name} zum Kunden befördern`}
                            title="Zum Kunden befördern"
                            onClick={() => setPromoting(o)}
                          >
                            <Handshake className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`${o.name} bearbeiten`}
                          onClick={() => setEditing(o)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`${o.name} löschen`}
                          onClick={() => setDeleting(o)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {rows.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {rows.length} {rows.length === 1 ? 'Firma' : 'Firmen'}
          {status !== 'alle' && ` · ${FILTER_LABELS[status]}`}
          {audienceId && ` in ${audienceName.get(audienceId) ?? ''}`}
        </p>
      )}

      {editing && (
        <OrganizationDialog
          organization={editing === 'new' ? null : editing}
          audiences={audiences}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      )}

      {deleting && (
        <DeleteOrganizationDialog
          organization={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={() => {
            setDeleting(null);
            void load();
          }}
        />
      )}

      {neuerKunde && (
        <NewCustomerDialog
          audiences={audiences}
          onClose={() => setNeuerKunde(false)}
          onDone={() => {
            setNeuerKunde(false);
            // Der Neuling soll sofort zu sehen sein, auch aus einem anderen Filter heraus.
            setStatus('kunde');
            void load();
          }}
        />
      )}

      {promoting && (
        <PromoteCustomerDialog
          organizationId={promoting.id}
          onClose={() => setPromoting(null)}
          onDone={() => {
            setPromoting(null);
            void load();
          }}
        />
      )}
    </div>
  );
}

/**
 * Löschen mit Rückfrage.
 *
 * Die Rückfrage nennt konkret, was dranhängt, und sagt ausdrücklich, dass
 * nichts davon mitgelöscht wird — sonst traut sich niemand.
 */
export function DeleteOrganizationDialog({
  organization,
  onClose,
  onDeleted,
}: {
  organization: { id: string; name: string };
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [impact, setImpact] = useState<{
    contacts: number;
    deals: number;
    orders: number;
    emails: number;
    projects: number;
  } | null>(null);
  // Kunde bleibt Kunde: `deleteOrganization` weigert sich, statt still ins
  // Leere zu laufen. Ohne diesen Zweig schlösse der Dialog trotzdem und die
  // Liste laedt neu — die Firma steht dann unveraendert wieder da, ohne dass
  // irgendwer erfahren hat, warum.
  const [blockiert, setBlockiert] = useState(false);

  useEffect(() => {
    void crm.organizations.deletionImpact(organization.id).then(setImpact);
  }, [organization.id]);

  return (
    <ConfirmDialog
      title={`${organization.name} löschen?`}
      confirmLabel="Löschen"
      destructive
      onCancel={onClose}
      onConfirm={async () => {
        const ergebnis = await crm.organizations.delete(organization.id);
        if (ergebnis.deleted) {
          onDeleted();
        } else if (ergebnis.reason === 'is_customer') {
          setBlockiert(true);
        } else {
          throw new Error('Diese Firma gibt es nicht mehr.');
        }
      }}
    >
      {impact === null ? (
        <p className="text-sm text-muted-foreground">Prüfe, was daran hängt …</p>
      ) : blockiert ? (
        <p className="rounded-md bg-secondary p-3 text-sm text-muted-foreground">
          {organization.name} ist Kunde — solange der Status steht, lässt sich
          die Firma nicht löschen
          {impact.projects > 0
            ? `, ${impact.projects} ${impact.projects === 1 ? 'Projekt hängt' : 'Projekte hängen'} daran`
            : ''}
          . Erst in der Firmenakte zurückstufen, dann lässt sich die Firma
          löschen.
        </p>
      ) : (
        <div className="space-y-2 text-sm text-muted-foreground">
          <p>
            Daran hängen{' '}
            <strong className="text-foreground">
              {impact.contacts} Kontakte, {impact.deals} Deals, {impact.orders}{' '}
              Aufträge, {impact.emails} E-Mails und {impact.projects}{' '}
              {impact.projects === 1 ? 'Projekt' : 'Projekte'}
            </strong>
            .
          </p>
          <p>
            Nichts davon wird gelöscht — alles bleibt bestehen und steht danach
            ohne Firma da. Nur die Firma selbst verschwindet.
          </p>
        </div>
      )}
    </ConfirmDialog>
  );
}
